import axios from 'axios';
import { ApiResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post<ApiResponse<any>>('/auth/login', {
      email,
      password,
    });
    return response.data;
  },
  // CR-17: TOTP two-factor auth
  verify2FALogin: async (tempToken: string, code: string) => {
    const response = await api.post<ApiResponse<{ user: any; token: string }>>('/auth/2fa/login', {
      tempToken,
      code,
    });
    return response.data;
  },
  setup2FA: async () => {
    const response = await api.post<ApiResponse<{ qrDataUrl: string; secret: string }>>('/auth/2fa/setup');
    return response.data;
  },
  verify2FA: async (code: string) => {
    const response = await api.post<ApiResponse<{ recoveryCodes: string[] }>>('/auth/2fa/verify', { code });
    return response.data;
  },
  disable2FA: async (password: string) => {
    const response = await api.post<ApiResponse>('/auth/2fa/disable', { password });
    return response.data;
  },
  logout: async () => {
    const response = await api.post<ApiResponse>('/auth/logout');
    return response.data;
  },
  getMe: async () => {
    const response = await api.get<ApiResponse<{ user: any }>>('/auth/me');
    return response.data;
  },
  changePassword: async (password: string) => {
    const response = await api.post<ApiResponse>('/auth/change-password', { password });
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post<ApiResponse>('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token: string, password: string) => {
    const response = await api.post<ApiResponse>('/auth/reset-password', {
      token,
      password,
    });
    return response.data;
  },
  inviteUser: async (email: string, role: string) => {
    const response = await api.post<ApiResponse>('/auth/invite', { email, role });
    return response.data;
  },
  acceptInvitation: async (token: string, password: string) => {
    const response = await api.post<ApiResponse<{ user: any; token: string }>>('/auth/accept-invitation', {
      token,
      password,
    });
    return response.data;
  },
  register: async (email: string, password: string, role?: string) => {
    const response = await api.post<ApiResponse<{ user: any; token: string }>>('/auth/register', {
      email,
      password,
      role: role || 'user',
    });
    return response.data;
  },
};

export const usersApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; role?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/users', { params });
    return response.data;
  },
  // Lightweight list (id/email/name/role) for select menus — available to all authenticated users
  getSelectable: async () => {
    const response = await api.get<ApiResponse<Array<{ id: string; email: string; role: string; firstName?: string | null; lastName?: string | null }>>>('/users/list');
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/users/${id}`);
    return response.data;
  },
  create: async (data: { email: string; password: string; role: string; firstName?: string; lastName?: string }) => {
    const response = await api.post<ApiResponse<any>>('/users', data);
    return response.data;
  },
  update: async (id: string, data: { email?: string; role?: string; firstName?: string; lastName?: string }) => {
    const response = await api.put<ApiResponse<any>>(`/users/${id}`, data);
    return response.data;
  },
  changePassword: async (id: string, password: string) => {
    const response = await api.put<ApiResponse<any>>(`/users/${id}/change-password`, { password });
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/users/${id}`);
    return response.data;
  },
  inviteUser: async ({ email, role }: { email: string; role: string }) => {
    const response = await api.post<ApiResponse>('/auth/invite', { email, role });
    return response.data;
  },
  // Invitation management
  getInvitations: async () => {
    const response = await api.get<ApiResponse<any[]>>('/auth/invitations');
    return response.data;
  },
  cancelInvitation: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/auth/invitations/${id}`);
    return response.data;
  },
  resendInvitation: async (id: string) => {
    const response = await api.post<ApiResponse>(`/auth/invitations/${id}/resend`);
    return response.data;
  },
};

export const projectsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; status?: string; country?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/projects', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/projects/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    notes?: string;
    country?: string;
    supplierId?: string;
    rdTriggerScore?: number;
    rdBlockDuplicates?: boolean;
    genericLink?: string;
    redirectUrl?: string;
    allowIdReuse?: boolean;
    projectManagerId?: string | null;
  }) => {
    const response = await api.post<ApiResponse<any>>('/projects', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    status?: 'open' | 'closed';
    country?: string;
    rdTriggerScore?: number;
    rdBlockDuplicates?: boolean;
    notes?: string;
    genericLink?: string;
    redirectUrl?: string;
    allowIdReuse?: boolean;
    projectManagerId?: string | null;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/projects/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/projects/${id}`);
    return response.data;
  },
  duplicate: async (id: string) => {
    const response = await api.post<ApiResponse<any>>(`/projects/${id}/duplicate`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get<ApiResponse<any>>('/projects/stats');
    return response.data;
  },
  getBuckets: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/buckets`);
    return response.data;
  },
  getSuppliers: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/suppliers`);
    return response.data;
  },
  getRespondents: async (projectId: string, params?: { limit?: number; offset?: number; search?: string }) => {
    const response = await api.get<ApiResponse<any>>(`/projects/${projectId}/respondents`, { params });
    return response.data;
  },
  getSupplierSummary: async (projectId: string) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/supplier-summary', {
      params: { projectId },
    });
    return response.data;
  },
  getCollectionSummary: async (projectId: string) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/collection-summary', {
      params: { projectId },
    });
    return response.data;
  },
  getMarketSummary: async (projectId: string) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/market-summary', {
      params: { projectId },
    });
    return response.data;
  },
  exportRespondents: async (projectId: string, params?: { format?: 'csv' | 'txt'; includeHeaders?: boolean }) => {
    const response = await api.get(`/projects/${projectId}/respondents/export`, { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
};

// CR-03: project groups (cross-project RD dedup via sy_gp_nr)
export const projectGroupsApi = {
  getAll: async () => {
    const response = await api.get<ApiResponse<any[]>>('/project-groups');
    return response.data;
  },
  create: async (name: string) => {
    const response = await api.post<ApiResponse<any>>('/project-groups', { name });
    return response.data;
  },
};

export const bucketsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; projectId?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/buckets', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/buckets/${id}`);
    return response.data;
  },
  create: async (data: {
    projectId: string;
    name: string;
    surveyUrl: string;
  }) => {
    const response = await api.post<ApiResponse<any>>('/buckets', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    surveyUrl?: string;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/buckets/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/buckets/${id}`);
    return response.data;
  },
  getByProject: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/buckets`);
    return response.data;
  },
};

export const projectSuppliersApi = {
  getAll: async (params?: { page?: number; limit?: number; projectId?: string; supplierId?: string; bucketId?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/project-suppliers', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/project-suppliers/${id}`);
    return response.data;
  },
  create: async (data: {
    projectId: string;
    supplierId: string;
    bucketId?: string;
    supplierRedirectUrl?: string;
    allowIdReuse?: boolean;
  }) => {
    const response = await api.post<ApiResponse<any>>('/project-suppliers', data);
    return response.data;
  },
  update: async (id: string, data: {
    bucketId?: string;
    supplierRedirectUrl?: string;
    allowIdReuse?: boolean;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/project-suppliers/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/project-suppliers/${id}`);
    return response.data;
  },
  getByProject: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/suppliers`);
    return response.data;
  },
};

export const respondentsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; projectSupplierId?: string; status?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/respondents', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/respondents/${id}`);
    return response.data;
  },
  create: async (data: {
    projectSupplierId: string;
    externalId: string;
  }) => {
    const response = await api.post<ApiResponse<any>>('/respondents', data);
    return response.data;
  },
  update: async (id: string, data: {
    status?: 'invited' | 'started' | 'completed' | 'terminated' | 'quota_full' | 'security_terminated' | 'quality_terminated';
    startedAt?: string;
    completedAt?: string;
    loiSeconds?: number;
    rdScore?: number;
    rdFlags?: Record<string, any>;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/respondents/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/respondents/${id}`);
    return response.data;
  },
  getByProjectSupplier: async (projectSupplierId: string) => {
    const response = await api.get<ApiResponse<any>>(`/project-suppliers/${projectSupplierId}/respondents`);
    return response.data;
  },
  getStats: async (projectSupplierId?: string) => {
    const response = await api.get<ApiResponse<any>>('/respondents/stats', { 
      params: projectSupplierId ? { projectSupplierId } : undefined 
    });
    return response.data;
  },
};

export const reportsApi = {
  // Basic report endpoints
  exportProjectData: async (params: {
    projectId: string;
    format?: 'csv' | 'txt';
    includeHeaders?: boolean;
    dateFrom?: string;
    dateTo?: string;
    statusFilter?: 'all' | 'completed' | 'terminated' | 'security_terminated' | 'quality_terminated';
    supplierId?: string;
    collectionId?: string;
    country?: string;
    bucketId?: string;
  }) => {
    const response = await api.get('/reports/project/export', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  exportScreenerData: async (params: {
    projectId: string;
    collectionId?: string;
  }) => {
    const response = await api.get(`/reports/project/${params.projectId}/screener-export`, {
      params: params.collectionId ? { collectionId: params.collectionId } : undefined,
      responseType: 'blob',
    });
    return response.data;
  },
  generateReconciliationReport: async (params: {
    projectId: string;
    dateFrom?: string;
    dateTo?: string;
    supplierId?: string;
    groupBy?: 'supplier' | 'bucket' | 'day' | 'status';
  }) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/reconciliation', { params });
    return response.data;
  },
  exportResearchDefenderData: async (params: {
    projectId: string;
    format?: 'csv' | 'txt';
    includeHeaders?: boolean;
    dateFrom?: string;
    dateTo?: string;
    supplierId?: string;
  }) => {
    const response = await api.get('/reports/project/rd-export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  getProjectStats: async (params: {
    projectId: string;
    period?: '24h' | '7d' | '30d' | '90d' | 'all';
    metrics?: ('completes' | 'loi' | 'ir' | 'rd_score' | 'cost')[];
  }) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/stats', { params });
    return response.data;
  },

  // Enhanced report endpoints
  getDashboardAnalytics: async (period: '24h' | '7d' | '30d' | '90d' = '30d') => {
    const response = await api.get<ApiResponse<any>>('/reports/dashboard', { params: { period } });
    return response.data;
  },
  getProjectAnalytics: async (params: {
    projectId: string;
    period?: '24h' | '7d' | '30d' | '90d';
  }) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/analytics', { params });
    return response.data;
  },
  getRealtimeStats: async (projectId: string) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/realtime', { 
      params: { projectId } 
    });
    return response.data;
  },
  getProjectsSummary: async () => {
    const response = await api.get<ApiResponse<any[]>>('/reports/projects/summary');
    return response.data;
  },
  getSupplierComparison: async (params?: {
    dateFrom?: string;
    dateTo?: string;
    projectId?: string;
  }) => {
    const response = await api.get<ApiResponse<any[]>>('/reports/suppliers/comparison', { params });
    return response.data;
  },
  getComprehensiveReport: async (params: {
    projectId: string;
    format?: 'json' | 'pdf';
    includeCharts?: boolean;
  }) => {
    const response = await api.get<ApiResponse<any>>('/reports/project/comprehensive', { params });
    return response.data;
  },

  // Failed attempts endpoints
  getFailedAttempts: async (params?: {
    projectId?: string;
    endpoint?: 'complete' | 'terminate' | 'quota' | 'security';
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get<ApiResponse<any>>('/reports/failed-attempts', { params });
    return response.data;
  },

  // Dropout reporting endpoints
  getDropoutLogs: async (params?: {
    projectId?: string;
    memberId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get<ApiResponse<any>>('/dropout/logs', { params });
    return response.data;
  },
  getDropoutStats: async (projectId?: string) => {
    const response = await api.get<ApiResponse<any>>('/dropout/stats', { 
      params: projectId ? { projectId } : undefined 
    });
    return response.data;
  },
  exportDropoutData: async (params: {
    format?: 'csv' | 'json';
    projectId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await api.get('/dropout/export', { 
      params,
      responseType: params.format === 'csv' ? 'blob' : undefined
    });
    return response.data;
  },

  // Reconciliation endpoint - upload file and match IDs against project respondents
  reconcileProject: async (projectId: string, file: File): Promise<{
    success: boolean;
    data?: {
      summary: {
        total: number;
        matched: number;
        unmatched: number;
        statusBreakdown: {
          completed: number;
          terminated: number;
          quota_full: number;
          security_terminated: number;
          quality_terminated: number;
          invited: number;
          started: number;
        };
      };
      matchedData: any[];
      matchedExternalIds: string[];
      unmatchedIds: string[];
    };
    error?: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/reports/project/${projectId}/reconcile`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Helper function to download files
  downloadFile: async (url: string, filename: string) => {
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
};

export const suppliersApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/suppliers', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/suppliers/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get<ApiResponse<any>>('/suppliers/stats');
    return response.data;
  },
  // CR-19: cross-project performance metrics for one supplier
  getMetrics: async (id: string) => {
    const response = await api.get<ApiResponse<any>>('/reports/supplier-metrics', {
      params: { supplierId: id },
    });
    return response.data;
  },
  // CR-19: performance metrics for every supplier (for the suppliers list + chart)
  getAllMetrics: async () => {
    const response = await api.get<ApiResponse<any[]>>('/reports/supplier-metrics');
    return response.data;
  },
  create: async (data: {
    name: string;
    placeholderToken?: string;
    completeUrlTemplate?: string;
    terminateUrlTemplate?: string;
    quotaFullUrlTemplate?: string;
    securityTerminateUrlTemplate?: string;
    apiConfig?: Record<string, any>;
  }) => {
    const response = await api.post<ApiResponse<any>>('/suppliers', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    placeholderToken?: string;
    completeUrlTemplate?: string;
    terminateUrlTemplate?: string;
    quotaFullUrlTemplate?: string;
    securityTerminateUrlTemplate?: string;
    apiConfig?: Record<string, any>;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/suppliers/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/suppliers/${id}`);
    return response.data;
  },
};

export const collectionsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; projectId?: string; country?: string }) => {
    const response = await api.get<ApiResponse<any[]>>('/collections', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/collections/${id}`);
    return response.data;
  },
  getByProject: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/collections`);
    return response.data;
  },
  create: async (data: {
    name: string;
    country: string;
    url: string;
    projectId: string;
  }) => {
    const response = await api.post<ApiResponse<any>>('/collections', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    country?: string;
    url?: string;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/collections/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/collections/${id}`);
    return response.data;
  },
  reorder: async (projectId: string, orderedIds: string[]) => {
    const response = await api.put<ApiResponse<any[]>>(
      `/projects/${projectId}/collections/order`,
      { orderedIds },
    );
    return response.data;
  },
};

export const contactsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; supplierId?: string; isPrimary?: boolean }) => {
    const response = await api.get<ApiResponse<any[]>>('/contacts', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/contacts/${id}`);
    return response.data;
  },
  getBySupplier: async (supplierId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/suppliers/${supplierId}/contacts`);
    return response.data;
  },
  create: async (data: {
    supplierId: string;
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    isPrimary?: boolean;
  }) => {
    const response = await api.post<ApiResponse<any>>('/contacts', data);
    return response.data;
  },
  update: async (id: string, data: {
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    isPrimary?: boolean;
  }) => {
    const response = await api.put<ApiResponse<any>>(`/contacts/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/contacts/${id}`);
    return response.data;
  },
};

export const qualityQuestionsApi = {
  getAll: async (params?: { isActive?: boolean }) => {
    const response = await api.get<ApiResponse<any[]>>('/quality-questions', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<any>>(`/quality-questions/${id}`);
    return response.data;
  },
  create: async (data: {
    type: 'multiple_choice' | 'open_ended';
    prompt: string;
    options?: Array<{ id?: string; label: string }> | null;
    correctOptionId?: string | null;
    isActive?: boolean;
  }) => {
    const response = await api.post<ApiResponse<any>>('/quality-questions', data);
    return response.data;
  },
  update: async (
    id: string,
    data: {
      type?: 'multiple_choice' | 'open_ended';
      prompt?: string;
      options?: Array<{ id?: string; label: string }> | null;
      correctOptionId?: string | null;
      isActive?: boolean;
    }
  ) => {
    const response = await api.put<ApiResponse<any>>(`/quality-questions/${id}`, data);
    return response.data;
  },
  deactivate: async (id: string) => {
    const response = await api.delete<ApiResponse<any>>(`/quality-questions/${id}`);
    return response.data;
  },
  getProjectAssignments: async (projectId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${projectId}/quality-questions`);
    return response.data;
  },
  setProjectAssignments: async (projectId: string, questionIds: string[]) => {
    const response = await api.put<ApiResponse<any[]>>(`/projects/${projectId}/quality-questions`, {
      questionIds,
    });
    return response.data;
  },
  getCollectionAssignments: async (collectionId: string) => {
    const response = await api.get<
      ApiResponse<any[]> & { meta?: { hasOverride?: boolean } }
    >(`/collections/${collectionId}/quality-questions`);
    return response.data;
  },
  setCollectionAssignments: async (collectionId: string, questionIds: string[]) => {
    const response = await api.put<
      ApiResponse<any[]> & { meta?: { hasOverride?: boolean } }
    >(`/collections/${collectionId}/quality-questions`, { questionIds });
    return response.data;
  },
};

export interface CleanupAffectedRow {
  id: string;
  maskedId: string;
  status: string;
  blockType: string;
  rdBlockReason: string | null;
  invitedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  projectSupplierId: string;
  collectionId: string | null;
}

export interface CleanupScanResponse {
  total: number;
  rowsReturned: number;
  rowLimit: number;
  byStatus: Record<string, number>;
  byBlockType: Record<string, number>;
  rows: CleanupAffectedRow[];
}

export const respondentCleanupApi = {
  scan: async () => {
    const response = await api.get<ApiResponse<CleanupScanResponse>>('/admin/respondent-cleanup/scan');
    return response.data;
  },
  apply: async (expectedCount: number) => {
    const response = await api.post<ApiResponse<{ updatedCount: number }>>(
      '/admin/respondent-cleanup/apply',
      { expectedCount }
    );
    return response.data;
  },
};

export default api;